import { useState, useEffect, useMemo } from 'react';

// D2 — animated status line shown while a single-shot /api/chat request is in
// flight (the Ask backend does not stream, so this IS the perceived-latency
// fix). Messages rotate every ~2.5s. CSS-only animation, gated on
// prefers-reduced-motion. Stops when the caller unmounts it (ChatPage swaps it
// for the answer the moment content arrives).

const CYCLE_MS = 2500;

// Default pool — warm, product-voiced. Rotated with no repeats per turn.
export const DEFAULT_POOL = [
  'Checking the Vault…',
  'Reading the manuals…',
  'Matching your vendor…',
  'Verifying sources…',
  'Lining up citations…',
  'Cross-checking the docs…',
];

// Neutral pool — plain forms only, used when the user's message touches
// sensitive/safety-critical territory (no cutesy copy over fire/life-safety).
export const NEUTRAL_POOL = [
  'Searching…',
  'Checking sources…',
  'Preparing your answer…',
];

// Whole-word matches so 'hr' doesn't fire on "through" and 'legal' doesn't fire
// on unrelated substrings. Hyphenated forms ("life-safety") match too.
const SENSITIVE_TERMS = [
  'fire',
  'life-safety',
  'life safety',
  'incident',
  'emergency',
  'legal',
  'hr',
];

export function isSensitive(text: string | undefined): boolean {
  const t = (text ?? '').toLowerCase();
  if (!t) return false;
  return SENSITIVE_TERMS.some((term) => {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`\\b${escaped}\\b`, 'i').test(t);
  });
}

export function poolFor(text: string | undefined): string[] {
  return isSensitive(text) ? NEUTRAL_POOL : DEFAULT_POOL;
}

// Fisher-Yates. Randomises the rotation order so consecutive turns don't always
// open with the same line; within a turn we walk the shuffled list in order, so
// no message repeats until the pool is exhausted.
function shuffle<T>(input: T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function ThinkingIndicator({ userText }: { userText?: string }) {
  const pool = useMemo(() => shuffle(poolFor(userText)), [userText]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
    const id = setInterval(() => {
      setIdx((prev) => (prev + 1) % pool.length);
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [pool]);

  const message = pool[idx % pool.length];

  return (
    <div className="thinking-indicator" role="status" aria-live="polite">
      <span className="thinking-dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      {/* key re-mounts the span so the fade re-triggers on each message */}
      <span key={message} className="thinking-text">
        {message}
      </span>
    </div>
  );
}
